<template>
    <div class="faq-container">
        <div class="faq-list">
            <div
                v-for="faq in faqs"
                :key="faq.id"
                class="faq-item"
                :class="{ 'is-open': isExpanded(faq) }"
            >
                <button
                    v-b-toggle="answerId(faq)"
                    type="button"
                    class="faq-item-content"
                >
                    <span class="faq-title">{{ faq.question }}</span>
                    <div class="faq-arrow">
                        <i class="fas" :class="isExpanded(faq) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                    </div>
                </button>
                <b-collapse
                    :id="answerId(faq)"
                    :accordion="`faqs-${idExtension}`"
                    :visible="isExpanded(faq)"
                    @show="expandedFaqId = faq.id"
                    @hide="onAnswerHide(faq)"
                >
                    <div class="faq-answer" v-html="faq.answer"></div>
                </b-collapse>
            </div>

            <div v-if="faqs.length === 0" class="no-faqs">
                <i class="fas fa-question-circle"></i>
                <p>No FAQs available</p>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'FaqsList',

    props: {
        faqs: {
            type: Array,
            required: true,
            default: () => []
        },
        idExtension: {
            type: String,
            default: ''
        },
        showMetadata: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            expandedFaqId: null
        }
    },

    methods: {
        onAnswerHide(faq) {
            // The accordion hides the previous answer after the new one starts showing, so only clear our own id
            if (this.isExpanded(faq)) {
                this.expandedFaqId = null
            }
        },

        isExpanded(faq) {
            return this.expandedFaqId === faq.id
        },

        answerId(faq) {
            return `faq-answer-${this.idExtension}-${faq.id}`
        }
    },

    watch: {
        faqs() {
            // Collapse any open answer when FAQs change (e.g., after search)
            this.expandedFaqId = null
        }
    }
}
</script>

<style scoped>
.faq-container {
    z-index: 1000;
}

/* FAQ List Styles */
.faq-list {
    height: 100%;
    overflow-y: auto;
    padding-top: 1px;
}

.faq-item {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    margin-bottom: 8px;
    background: white;
    transition: all 0.2s ease;
}

.faq-item:hover {
    border-color: #007bff;
    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.1);
}

.faq-item.is-open {
    border-color: #007bff;
}

.faq-item-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    padding: 16px 20px;
    border: none;
    background: none;
    text-align: left;
    cursor: pointer;
}

.faq-item-content:focus {
    outline: 2px solid #007bff;
    outline-offset: 2px;
    border-radius: 8px;
}

.faq-title {
    margin: 0;
    font-size: 16px;
    font-weight: 500;
    color: #333;
    flex: 1;
    line-height: 1.4;
}

.faq-arrow {
    color: #999;
    font-size: 14px;
    margin-left: 12px;
    transition: all 0.2s ease;
}

.faq-item:hover .faq-arrow,
.faq-item.is-open .faq-arrow {
    color: #007bff;
}

.no-faqs {
    text-align: center;
    padding: 40px 20px;
    color: #666;
}

.no-faqs i {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
}

.no-faqs p {
    font-size: 16px;
    margin: 0;
}

/* FAQ Answer Styles */
.faq-answer {
    max-height: 300px;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 0 20px 16px;
    color: #555;
    line-height: 1.6;
    font-size: 15px;
}

/* Style HTML content in answers */
.faq-answer :deep(p) {
    margin: 0 0 16px 0;
}

.faq-answer :deep(p:last-child) {
    margin-bottom: 0;
}

.faq-answer :deep(ul),
.faq-answer :deep(ol) {
    margin: 16px 0;
    padding-left: 24px;
}

.faq-answer :deep(li) {
    margin-bottom: 8px;
}

.faq-answer :deep(strong),
.faq-answer :deep(b) {
    font-weight: 600;
    color: #333;
}

.faq-answer :deep(a) {
    color: #007bff;
    text-decoration: none;
}

.faq-answer :deep(a:hover) {
    text-decoration: underline;
}

.faq-answer :deep(h1),
.faq-answer :deep(h2),
.faq-answer :deep(h3),
.faq-answer :deep(h4),
.faq-answer :deep(h5),
.faq-answer :deep(h6) {
    margin: 24px 0 16px 0;
    font-weight: 600;
    color: #333;
}

.faq-answer :deep(h1:first-child),
.faq-answer :deep(h2:first-child),
.faq-answer :deep(h3:first-child),
.faq-answer :deep(h4:first-child),
.faq-answer :deep(h5:first-child),
.faq-answer :deep(h6:first-child) {
    margin-top: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
    .faq-item-content {
        padding: 14px 16px;
    }

    .faq-title {
        font-size: 15px;
    }

    .faq-answer {
        padding: 0 16px 14px;
        font-size: 14px;
    }
}

@media (max-width: 480px) {
    .faq-item-content {
        padding: 12px 14px;
    }

    .faq-title {
        font-size: 14px;
    }

    .faq-answer {
        padding: 0 14px 12px;
    }
}

/* Animation for reduced motion users */
@media (prefers-reduced-motion: reduce) {
    .faq-item,
    .faq-arrow {
        transition: none;
    }
}

/* High contrast mode */
@media (prefers-contrast: high) {
    .faq-item {
        border-width: 2px;
        border-color: #000;
    }

    .faq-item:hover {
        border-color: #0066cc;
        background-color: #f0f8ff;
    }
}
</style>



// WEBPACK FOOTER //
// src/Assets/components/Faqs/List.vue